import Navbar from "@/components/Layout/Navbar";
import Footer from "@/components/Layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowLeft, Calendar, Clock, Check } from "lucide-react";
import { Link } from "@/lib/router-compat";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroImage from "@/assets/blog/nearshore-guide-hero.jpg";
import SEOHead from "@/components/SEOHead";
import { BASE_URL } from "@/lib/seo";
import { useTranslation } from "react-i18next";
import { useLanguageRouting } from "@/hooks/useLanguageRouting";
import AuthorBio, { AUTHORS } from "@/components/EEAT/AuthorBio";
import ArticleMeta from "@/components/EEAT/ArticleMeta";
import SourcesList from "@/components/EEAT/SourcesList";
import TrustBar from "@/components/EEAT/TrustBar";

const NearshoreOutsourcingGuide = () => {
  const { t, i18n } = useTranslation(['blog', 'common']);
  const { getLocalizedLink } = useLanguageRouting();
  const currentLang = i18n.language;

  const getArrayTranslation = (key: string): string[] => {
    const result = t(key, { returnObjects: true });
    if (Array.isArray(result)) {
      return result.filter((item): item is string => typeof item === 'string');
    }
    return [];
  };

  const faqItems = [
    { question: t('blog:nearshoreGuide.faq.q1.question'), answer: t('blog:nearshoreGuide.faq.q1.answer') },
    { question: t('blog:nearshoreGuide.faq.q2.question'), answer: t('blog:nearshoreGuide.faq.q2.answer') },
    { question: t('blog:nearshoreGuide.faq.q3.question'), answer: t('blog:nearshoreGuide.faq.q3.answer') },
    { question: t('blog:nearshoreGuide.faq.q4.question'), answer: t('blog:nearshoreGuide.faq.q4.answer') },
    { question: t('blog:nearshoreGuide.faq.q5.question'), answer: t('blog:nearshoreGuide.faq.q5.answer') },
  ];

  const countries = [
    { name: t('blog:nearshoreGuide.sections.countries.egypt.name'), highlights: getArrayTranslation('blog:nearshoreGuide.sections.countries.egypt.highlights') },
    { name: t('blog:nearshoreGuide.sections.countries.southAfrica.name'), highlights: getArrayTranslation('blog:nearshoreGuide.sections.countries.southAfrica.highlights') },
    { name: t('blog:nearshoreGuide.sections.countries.morocco.name'), highlights: getArrayTranslation('blog:nearshoreGuide.sections.countries.morocco.highlights') },
    { name: t('blog:nearshoreGuide.sections.countries.kenya.name'), highlights: getArrayTranslation('blog:nearshoreGuide.sections.countries.kenya.highlights') },
  ];

  const evolviceUrl = currentLang === 'de' ? 'https://www.evolvice.de' : 'https://www.evolvice.de/en';

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        titleEn="Nearshore Outsourcing Guide: Africa | 2026"
        titleDe="Nearshore-Outsourcing Leitfaden: Afrika | 2026"
        descriptionEn="Guide to nearshore software outsourcing in Africa. Benefits, top destinations, and best practices."
        descriptionDe="Umfassender Leitfaden zum Nearshore-Software-Outsourcing in Afrika. Erfahren Sie mehr über Vorteile, Herausforderungen, Top-Destinationen wie Ägypten, Kenia und Marokko sowie Best Practices für den Erfolg."
        keywords="nearshore outsourcing Africa, software development outsourcing, Egypt software development, Kenya tech hub, Morocco IT services, African software talent"
        path="/blog/nearshore-outsourcing-guide"
        type="article"
        publishedTime="2026-01-10T00:00:00Z"
        modifiedTime="2026-04-19T00:00:00Z"
        author={AUTHORS["sherif-adel"].nameEn}
        article={{
          title: t('blog:nearshoreGuide.title'),
          description: t('blog:nearshoreGuide.intro'),
          url: `${BASE_URL}${currentLang === 'de' ? '' : '/en'}/blog/nearshore-outsourcing-guide`,
          image: `${BASE_URL}${heroImage}`,
          datePublished: "2026-01-10",
          dateModified: "2026-04-19",
          author: AUTHORS["sherif-adel"].nameEn,
          authorJobTitle: AUTHORS["sherif-adel"].roleEn,
          authorUrl: AUTHORS["sherif-adel"].linkedinUrl,
          reviewer: AUTHORS["evolvice-editorial"].nameEn,
        }}
        breadcrumbs={[
          { name: 'Home', nameDE: 'Startseite', url: '/' },
          { name: 'Blog', nameDE: 'Blog', url: '/blog' },
          { name: 'Nearshore Outsourcing Guide', nameDE: 'Nearshore-Outsourcing-Leitfaden', url: '/blog/nearshore-outsourcing-guide' }
        ]}
        faqs={faqItems}
      />

      <Navbar />

      <main className="pt-24 pb-32">
        <div className="container mx-auto px-6 max-w-3xl">
          <Button variant="ghost" className="mb-10 group -ml-3" asChild>
            <Link to={getLocalizedLink('/blog')}>
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              {t('blog:backToBlog')}
            </Link>
          </Button>

          <article>
            <header className="mb-12">
              <div className="flex items-center gap-4 text-xs uppercase tracking-[0.15em] text-muted-foreground mb-6 flex-wrap">
                <span className="px-3 py-1 rounded-full border border-border/60 bg-background/60 text-foreground/80">
                  {t('blog:nearshoreGuide.category')}
                </span>
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{t('blog:nearshoreGuide.date')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  <span>{t('blog:nearshoreGuide.readTime')} {t('blog:minRead')}</span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl font-semibold tracking-[-0.035em] text-balance mb-6 leading-tight">
                {t('blog:nearshoreGuide.title')}
              </h1>

              <ArticleMeta publishedDate="2026-01-10" updatedDate="2026-04-19" />

              <AuthorBio
                authorId="sherif-adel"
                reviewedByDe={AUTHORS["evolvice-editorial"].nameDe}
                reviewedByEn={AUTHORS["evolvice-editorial"].nameEn}
              />

              <div className="rounded-2xl overflow-hidden mb-8 border border-border/60">
                <img src={heroImage} alt="Nearshore outsourcing in Africa" className="w-full h-auto" />
              </div>

              <p className="text-xl text-foreground/80 leading-relaxed">
                {t('blog:nearshoreGuide.intro')}
              </p>
            </header>

            <div className="max-w-none">
              <section className="mb-16">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Context</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-5">{t('blog:nearshoreGuide.sections.rise.title')}</h2>
                <p className="text-foreground/80 leading-relaxed mb-4">{t('blog:nearshoreGuide.sections.rise.p1')}</p>
                <p className="text-foreground/80 leading-relaxed mb-5">{t('blog:nearshoreGuide.sections.rise.p2')}</p>
                <ul className="space-y-2 mb-5">
                  {getArrayTranslation('blog:nearshoreGuide.sections.rise.list').map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-foreground/80">
                      <Check className="w-4 h-4 text-foreground/40 mt-1 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-foreground/80 leading-relaxed">{t('blog:nearshoreGuide.sections.rise.p3')}</p>
              </section>

              <section className="mb-16 pt-12 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Benefits</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-8">{t('blog:nearshoreGuide.sections.benefits.title')}</h2>
                <div className="space-y-3">
                  {['costEffectiveness', 'timezone', 'talent', 'culture', 'multilingual'].map((key, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-7">
                        <div className="flex items-start gap-5">
                          <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-medium pt-1 min-w-[1.5rem]">
                            {String(idx + 1).padStart(2, '0')}
                          </span>
                          <div>
                            <h3 className="text-lg font-semibold tracking-[-0.02em] mb-2">{t(`blog:nearshoreGuide.sections.benefits.${key}.title`)}</h3>
                            <p className="text-muted-foreground leading-relaxed">{t(`blog:nearshoreGuide.sections.benefits.${key}.description`)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="mb-16 pt-12 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Considerations</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-5">{t('blog:nearshoreGuide.sections.challenges.title')}</h2>
                <p className="text-foreground/80 leading-relaxed mb-8">{t('blog:nearshoreGuide.sections.challenges.intro')}</p>
                <div className="grid md:grid-cols-2 gap-5">
                  {['infrastructure', 'skillGap', 'stability', 'security'].map((key, idx) => (
                    <Card key={idx}>
                      <CardContent className="p-7">
                        <h3 className="text-lg font-semibold tracking-[-0.02em] mb-3">{t(`blog:nearshoreGuide.sections.challenges.${key}.title`)}</h3>
                        <p className="text-muted-foreground leading-relaxed text-sm">{t(`blog:nearshoreGuide.sections.challenges.${key}.description`)}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="mb-16 pt-12 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Destinations</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-8">{t('blog:nearshoreGuide.sections.countries.title')}</h2>
                <div className="space-y-4">
                  {countries.map((country, index) => (
                    <Card key={index}>
                      <CardContent className="p-7">
                        <h3 className="text-xl font-semibold tracking-[-0.02em] mb-4">{country.name}</h3>
                        <ul className="space-y-2">
                          {country.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-3 text-muted-foreground text-sm">
                              <Check className="w-4 h-4 text-foreground/40 mt-0.5 flex-shrink-0" />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </section>

              <section className="mb-16 pt-12 border-t border-border/60">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">FAQ</div>
                <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-6">{t('blog:nearshoreGuide.faq.title')}</h2>
                <Accordion type="single" collapsible className="w-full">
                  {faqItems.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border-border/60">
                      <AccordionTrigger className="text-left text-base font-medium tracking-[-0.015em] hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>

              <SourcesList
                sources={[
                  { publisher: "Stack Overflow", label: "Developer Survey 2024 — Salaries", url: "https://survey.stackoverflow.co/2024/work#salary-comp-total", year: 2024 },
                  { publisher: "ITIDA Egypt", label: "Egypt's IT Industry Profile 2024", url: "https://itida.gov.eg/", year: 2024 },
                  { publisher: "Statista", label: "IT services market in Africa", url: "https://www.statista.com/outlook/tmo/it-services/africa", year: 2025 },
                  { publisher: "World Bank", label: "Digital Economy in Africa", url: "https://www.worldbank.org/en/programs/all-africa-digital-transformation", year: 2024 },
                  { publisher: "European Commission", label: "GDPR — Standard Contractual Clauses", url: "https://commission.europa.eu/law/law-topic/data-protection/international-dimension-data-protection/standard-contractual-clauses-scc_en", year: 2024 },
                ]}
              />

              <div className="my-12">
                <TrustBar />
              </div>

              <Card className="text-center">
                <CardContent className="py-14 px-8">
                  <h2 className="text-3xl font-semibold tracking-[-0.028em] mb-4">{t('blog:nearshoreGuide.cta.title')}</h2>
                  <p className="text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto">{t('blog:nearshoreGuide.cta.subtitle')}</p>
                  <Button size="lg" asChild>
                    <a href={evolviceUrl} target="_blank" rel="noopener noreferrer">
                      {t('blog:nearshoreGuide.cta.button')}
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default NearshoreOutsourcingGuide;
